/**
 * 任务工作流面板
 * 重构版本 - 使用新的组件架构
 * Requirements: 2.5, 2.7, 3.2, 3.4, 3.6, 4.1, 4.3, 5.1, 5.4
 */

import { useState } from 'react'

import { DeadlineDisplay } from '@/components/pm/issue/deadline-display'
import { StageReviewerPanel } from '@/components/pm/issue/stage-reviewer-panel'
import {
  useIssueWorkflow,
  useTimeConfig,
  useTeamStageOwners,
  useTeamMembers,
  useRejectWorkflow,
  type WorkflowStage,
} from '@/hooks'

import { RollbackDialog } from './dialogs/rollback-dialog'
import { ParticipantsSection } from './sections/participants-section'
import { TimeEstimationSection } from './sections/time-estimation-section'
import { WorkflowProgressSection } from './sections/workflow-progress-section'
import { WorkflowProvider } from './workflow-context'
import { WorkflowEmptyState } from './workflow-empty-state'
import { getEnabledStages, getRollbackStages } from './workflow-utils'

// ==================== 类型定义 ====================

interface WorkflowPanelProps {
  issueId: string
  issueTitle: string
  teamId?: string
  isTeamLeader?: boolean
}

// ==================== 组件实现 ====================

export function WorkflowPanel({
  issueId,
  issueTitle,
  teamId,
  isTeamLeader = false,
}: WorkflowPanelProps) {
  const { data: config, isLoading, error } = useIssueWorkflow(issueId)
  const { data: timeConfig } = useTimeConfig(issueId)
  const { data: stageOwners } = useTeamStageOwners(teamId ?? '')
  const { data: teamMembers } = useTeamMembers(teamId ?? '')

  const [showRejectDialog, setShowRejectDialog] = useState(false)

  const rejectMutation = useRejectWorkflow()

  // 根据员工ID获取员工姓名
  const getEmployeeName = (employeeId: string | null): string | null => {
    if (!employeeId || !teamMembers) {
      return null
    }

    const allMembers = [
      ...(teamMembers.developers?.frontend || []),
      ...(teamMembers.developers?.backend || []),
      ...(teamMembers.developers?.design || []),
      ...(teamMembers.developers?.testing || []),
      ...(teamMembers.developers?.product || []),
    ]
    const member = allMembers.find((m) => m.employeeId === employeeId)
    return member?.employee?.name || null
  }

  // 加载状态
  if (isLoading) {
    return <WorkflowEmptyState type="loading" />
  }

  // 错误状态
  if (error) {
    return <WorkflowEmptyState type="error" />
  }

  // 未配置工作流
  if (!config) {
    return <WorkflowEmptyState type="not-configured" />
  }

  const enabledStages = getEnabledStages(config)
  const rollbackStages = getRollbackStages(config, enabledStages)

  // 处理回退
  const handleReject = (targetStage: WorkflowStage, reason: string) => {
    rejectMutation.mutate(
      { issueId, data: { targetStage, reason } },
      { onSuccess: () => setShowRejectDialog(false) }
    )
  }

  return (
    <WorkflowProvider
      issueId={issueId}
      issueTitle={issueTitle}
      teamId={teamId}
      isTeamLeader={isTeamLeader}
    >
      <div className="space-y-4">
        {/* 工作流进度（含阶段操作和轨道操作） */}
        <WorkflowProgressSection
          issueId={issueId}
          config={config}
          timeConfig={timeConfig}
          teamId={teamId}
          stageOwners={stageOwners}
          getEmployeeName={getEmployeeName}
          onShowReject={() => setShowRejectDialog(true)}
        />

        {/* 截止日期显示 - Requirements: 3.4, 4.5 */}
        {timeConfig && timeConfig.stageEstimates.length > 0 && (
          <DeadlineDisplay
            issueId={issueId}
            currentStage={config.currentStage}
            showAllStages={false}
          />
        )}

        {/* 工时估算配置 - Requirements: 2.5, 2.7 */}
        <TimeEstimationSection
          issueId={issueId}
          enabledStages={enabledStages}
          developmentConfig={config.developmentConfig}
          timeConfig={timeConfig}
        />

        {/* 阶段参与人员 */}
        <ParticipantsSection issueId={issueId} currentStage={config.currentStage} />

        {/* 阶段审核人管理 */}
        <StageReviewerPanel
          issueId={issueId}
          config={config}
          teamId={teamId}
          isTeamLeader={isTeamLeader}
        />

        {/* 回退对话框 */}
        <RollbackDialog
          open={showRejectDialog}
          onOpenChange={setShowRejectDialog}
          issueTitle={issueTitle}
          currentStage={config.currentStage}
          rollbackStages={rollbackStages}
          onConfirm={handleReject}
          loading={rejectMutation.isPending}
          error={rejectMutation.error?.message}
        />
      </div>
    </WorkflowProvider>
  )
}

// Re-export commonly used types and components
export type { WorkflowPanelProps }
